import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, MoreVertical, Search, Plus } from 'lucide-react';
import { formatDate } from '../../utils/converter';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { useUserContext } from '../../contexts/UserContext';
import { getOrgLevels } from '../../utils/APIs';
import useComponentVisible from '../UI/ComponentVisible';
import AddOrgLevelModal from './AddOrgLevelModal';
import { Button, Input, AgTableCard } from '../UI';

const OrgLevels = () => {
    const navigate = useNavigate();
    const { content } = useContentfulContext();
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

    const [gridApi, setGridApi] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);

    const { userData } = useUserContext();

    const getAllTasks = async () => {
        try {
            setLoading(true);
            const res = await getOrgLevels(userData?.token, userData?.selectedOrganization?.org_token);
            setRowData(res.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllTasks();
    }, []);

    const colDefs = [
        {
            headerName: content.ORGLEVELS.table_headers.level,
            field: "id",
            cellRenderer: ({ data }) => (
                <span className="stride-badge-pill bg-purple-50 text-purple-700 border border-purple-200 text-xs font-semibold">
                    Level {data.id}
                </span>
            ),
            filter: true,
            sortable: true,
            suppressMovable: true,
        },
        {
            headerName: content.ORGLEVELS.table_headers.createdAt,
            field: "createdAt",
            cellRenderer: ({ data }) => (
                <span className="text-xs text-stride-muted tabular-nums">
                    {formatDate(data.createdAt)}
                </span>
            ),
            filter: true,
            sortable: true,
            suppressMovable: true,
        },
        {
            headerName: content.ORGLEVELS.table_headers.route_permission,
            field: "route_permission",
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="font-medium text-xs text-primary hover:text-primary-hover hover:underline cursor-pointer"
                    onClick={() => {
                        navigate(`/admin/org-level/${data._id}`, { state: { data, property: "route_permission" } });
                    }}
                >
                    View details
                </button>
            ),
            suppressMovable: true,
        },
        {
            headerName: content.ORGLEVELS.table_headers.feature_permission,
            field: "feature_permission",
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="font-medium text-xs text-primary hover:text-primary-hover hover:underline cursor-pointer"
                    onClick={() => {
                        navigate(`/admin/org-level/${data._id}`, { state: { data, property: "feature_permission" } });
                    }}
                >
                    View details
                </button>
            ),
            suppressMovable: true,
        },
        {
            headerName: content.ORGLEVELS.table_headers.feature_restriction,
            field: "feature_restriction",
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="font-medium text-xs text-primary hover:text-primary-hover hover:underline cursor-pointer"
                    onClick={() => {
                        navigate(`/admin/org-level/${data._id}`, { state: { data, property: "feature_restriction" } });
                    }}
                >
                    View details
                </button>
            ),
            suppressMovable: true,
        },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 100,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => (
                <div className="stride-table-actions">
                    <button
                        type="button"
                        title="Copy Org Level"
                        className="stride-table-btn stride-table-btn-copy"
                        onClick={() => {
                            setSelectedLevel(data);
                            setShowCopyModal(true);
                        }}
                    >
                        <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        className="stride-table-btn actions"
                        title="More options"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsComponentVisible(!isComponentVisible);
                            setSelectedLevel(data);
                            const buttonRect = e.currentTarget.getBoundingClientRect();
                            setMenuPosition({
                                top: buttonRect.bottom + window.scrollY,
                                left: buttonRect.left + window.scrollX
                            });
                        }}
                    >
                        <MoreVertical className="w-3.5 h-3.5" />
                    </button>
                </div>
            ),
        }
    ];

    const onFilterTextBoxChanged = (event) => {
        const filterValue = (event.target.value || '').toLowerCase();
        gridApi.setFilterModel({
            id: { type: 'contains', filter: filterValue }
        });
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    const autoSizeStrategy = useMemo(() => ({
        type: "fitGridWidth",
        defaultMinWidth: 120,
        columnLimits: [
            { colId: "id", minWidth: 120 },
            { colId: "createdAt", minWidth: 160 },
            { colId: "route_permission", minWidth: 180 },
            { colId: "feature_permission", minWidth: 180 },
            { colId: "feature_restriction", minWidth: 180 },
            { colId: "actions", minWidth: 100 },
        ],
    }), []);

    return (
        <>
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <span className="stride-eyebrow">Administration</span>
                        <div className="flex items-baseline gap-3">
                            <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                                {content.ORGLEVELS.title}
                            </h1>
                            <span className="text-xs text-stride-muted font-normal">
                                {rowData.length} {rowData.length === 1 ? 'level' : 'levels'}
                            </span>
                        </div>
                    </div>

                    <Button
                        variant="primary"
                        icon={<Plus className="w-4 h-4" />}
                        onClick={() => {
                            setSelectedLevel(null);
                            setShowAddModal(true);
                        }}
                    >
                        {content.ORGLEVELS.add_modal.cta_text || 'Add Org Level'}
                    </Button>
                </div>

                <AgTableCard
                    header={
                        <div className="w-full sm:w-80">
                            <Input
                                placeholder={content.ORGLEVELS.search_placeholder}
                                onChange={onFilterTextBoxChanged}
                                prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                clearable
                            />
                        </div>
                    }
                    tableId="org-levels-table"
                    loading={loading}
                    rowData={rowData}
                    columnDefs={colDefs}
                    onGridReady={onGridReady}
                    autoSizeStrategy={autoSizeStrategy}
                >
                    {isComponentVisible && (
                        <div
                            ref={ref}
                            className="absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1"
                            style={{ top: menuPosition.top, left: Math.max(10, menuPosition.left - 180) }}
                        >
                            <div className="py-1">
                                <button
                                    type="button"
                                    className="text-gray-700 block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                    onClick={() => {
                                        setIsComponentVisible(false);
                                        setShowCopyModal(true);
                                    }}
                                >
                                    {content.ORGLEVELS.more_info.copy || 'Copy'}
                                </button>
                            </div>
                        </div>
                    )}
                </AgTableCard>
            </main>

            {showAddModal && (
                <AddOrgLevelModal
                    hideModal={setShowAddModal}
                    onSuccess={getAllTasks}
                />
            )}

            {showCopyModal && (
                <AddOrgLevelModal
                    hideModal={setShowCopyModal}
                    onSuccess={getAllTasks}
                    copyFrom={selectedLevel}
                />
            )}
        </>
    );
};

export default OrgLevels;
